import React, { useState, useEffect } from "react";
import { CheckCircle, Loader2, Percent, CreditCard, Tag, LogIn } from "lucide-react";
import BalloonCelebration from "../BalloonCelebration";
import PaymentTrustBadge from "../PaymentTrustBadge";

export const PriceSummary = ({ pkgPrice, addOnsTotal, getBreakdown, getDiscountAmount, getAmount, referralValid, couponValid, couponDiscount, formData, earlyBird, earlyBirdDiscount = 0 }) => (
  <div className="bg-[#FFFAF5] rounded-xl p-3 border border-[#f0e6d9] space-y-1" data-testid="price-summary">
    {pkgPrice > 0 && (() => {
      const { decoration, emf, gst } = getBreakdown(pkgPrice);
      return (
        <>
          <div className="flex justify-between text-xs text-[#475569]">
            <span>Decoration</span><span>Rs {Math.round(decoration).toLocaleString()}</span>
          </div>
          <div className="flex justify-between text-xs text-[#475569]">
            <span>Event Management Fee (15%)</span><span>Rs {Math.round(emf).toLocaleString()}</span>
          </div>
          <div className="flex justify-between text-xs text-[#475569]">
            <span>GST (18%)</span><span>Rs {Math.round(gst).toLocaleString()}</span>
          </div>
          <div className="flex justify-between text-xs text-[#475569] font-medium pt-1 border-t border-[#e5ddd3]">
            <span>{formData.package} Package</span><span>Rs {pkgPrice.toLocaleString()}</span>
          </div>
        </>
      );
    })()}
    {addOnsTotal > 0 && (
      <div className="flex justify-between text-xs text-[#475569]">
        <span>Add-ons</span><span>Rs {addOnsTotal.toLocaleString()}</span>
      </div>
    )}
    {(referralValid || (couponValid && couponDiscount)) && (
      <div className="flex justify-between text-xs text-[#25D366] font-medium">
        <span className="flex items-center gap-1"><Percent size={11} /> Discount</span>
        <span>
          {referralValid ? `- Rs ${getDiscountAmount().toLocaleString()} (5% on total)` :
           couponDiscount?.discount_type === 'flat' ? `- Rs ${couponDiscount.discount_amount.toLocaleString()}` :
           `- Rs ${getDiscountAmount().toLocaleString()} (${couponDiscount?.discount_percent}% on EMF)`}
        </span>
      </div>
    )}
    {earlyBirdDiscount > 0 && earlyBird?.active && (
      <div className="flex justify-between text-xs text-[#92400E] font-semibold" data-testid="early-bird-line">
        <span className="flex items-center gap-1">
          <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-amber-400 text-white text-[9px] font-bold">★</span>
          Launch offer
        </span>
        <span>- Rs {earlyBirdDiscount.toLocaleString()}</span>
      </div>
    )}
    <div className="flex justify-between text-sm font-bold text-[#1E1B4B] pt-1 border-t border-[#e5ddd3]">
      <span>You Pay</span><span className="text-[#E83E8C]">Rs {getAmount().toLocaleString()}</span>
    </div>
  </div>
);

export const PaymentOptions = ({ formData, setFormData, ADVANCE_AMOUNT, pkgPrice, addOnsTotal }) => (
  <div>
    <label className="block text-sm font-medium text-[#1E1B4B] mb-2 flex items-center gap-2"><CreditCard size={16} /> Payment Option *</label>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <button type="button" onClick={() => setFormData({ ...formData, payment_type: 'advance' })} className={`p-3 sm:p-4 rounded-xl border-2 transition-all ${formData.payment_type === 'advance' ? 'border-[#E83E8C] bg-[#E83E8C]/5' : 'border-gray-200'}`} data-testid="payment-advance-btn">
        <p className="font-semibold text-[#1E1B4B] text-sm">Pay Advance</p><p className="text-xl sm:text-2xl font-bold text-[#E83E8C]">Rs {ADVANCE_AMOUNT.toLocaleString()}</p><p className="text-xs text-[#475569]">Balance on setup day</p>
      </button>
      <button type="button" onClick={() => setFormData({ ...formData, payment_type: 'full' })} className={`p-3 sm:p-4 rounded-xl border-2 transition-all ${formData.payment_type === 'full' ? 'border-[#E83E8C] bg-[#E83E8C]/5' : 'border-gray-200'}`} data-testid="payment-full-btn">
        <p className="font-semibold text-[#1E1B4B] text-sm">Pay Full Amount</p><p className="text-xl sm:text-2xl font-bold text-[#25D366]">Rs {(pkgPrice + addOnsTotal).toLocaleString()}</p><p className="text-xs text-[#475569]">{formData.package === 'Custom' ? 'Total of selections' : 'Includes add-ons'}</p>
      </button>
    </div>
    {/* ₹1 test payment — team sanity-check for the gateway. Skips side-effects on the backend. */}
    <button
      type="button"
      onClick={() => setFormData({ ...formData, payment_type: formData.payment_type === 'test' ? 'advance' : 'test' })}
      className={`mt-2 w-full px-3 py-2 rounded-lg border text-xs transition-all flex items-center justify-between ${formData.payment_type === 'test' ? 'border-dashed border-[#E83E8C] bg-[#E83E8C]/5 text-[#E83E8C]' : 'border-dashed border-gray-300 bg-gray-50 text-[#475569] hover:border-[#E83E8C]/60 hover:text-[#E83E8C]'}`}
      data-testid="payment-test-btn"
    >
      <span className="font-medium">Team testing? Send a Rs 1 test payment</span>
      <span className={`inline-flex items-center justify-center w-4 h-4 rounded-full border ${formData.payment_type === 'test' ? 'border-[#E83E8C] bg-[#E83E8C] text-white' : 'border-gray-300'}`}>
        {formData.payment_type === 'test' && <CheckCircle size={10} />}
      </span>
    </button>
  </div>
);

export const DiscountSection = ({ formData, setFormData, referralValid, referralMessage, couponValid, couponMessage, validateReferralCode, validateCouponCode }) => (
  <div className="bg-gradient-to-r from-[#E83E8C]/5 to-[#FF9F1C]/5 rounded-xl p-4 border border-[#E83E8C]/10 space-y-3">
    <p className="text-xs font-semibold text-[#1E1B4B] flex items-center gap-1.5"><Tag size={13} /> Apply Discount</p>
    <div>
      <label className="block text-xs text-[#475569] mb-1">Referral Code</label>
      <input type="text" value={formData.referral_code}
        onChange={(e) => { const code = e.target.value.toUpperCase(); setFormData({ ...formData, referral_code: code }); if (code.length >= 5) validateReferralCode(code); else {} }}
        className={`w-full px-3 py-2.5 rounded-xl border-2 outline-none transition-all text-sm ${referralValid === true ? 'border-[#25D366] bg-[#25D366]/5' : referralValid === false ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}
        placeholder="e.g. DOOZY-PRIYA23" data-testid="referral-code-input"
        disabled={couponValid === true} />
      {referralMessage && (<p className={`text-xs mt-1 font-medium ${referralValid ? 'text-[#25D366]' : 'text-red-500'}`}>{referralValid ? '\u2713' : '\u2717'} {referralMessage}</p>)}
    </div>
    <div className="flex items-center gap-2 text-xs text-[#94a3b8]">
      <div className="flex-1 h-px bg-gray-200" /><span>OR</span><div className="flex-1 h-px bg-gray-200" />
    </div>
    <div>
      <label className="block text-xs text-[#475569] mb-1">Coupon Code</label>
      <div className="flex gap-2">
        <input type="text" value={formData.coupon_code}
          onChange={(e) => { const code = e.target.value.toUpperCase(); setFormData({ ...formData, coupon_code: code }); }}
          className={`flex-1 px-3 py-2.5 rounded-xl border-2 outline-none transition-all text-sm font-mono ${couponValid === true ? 'border-[#25D366] bg-[#25D366]/5' : couponValid === false ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}
          placeholder="e.g. EASTER50" data-testid="coupon-code-input"
          disabled={referralValid === true} />
        <button type="button" onClick={() => validateCouponCode(formData.coupon_code)}
          disabled={!formData.coupon_code || referralValid === true}
          className="px-4 py-2.5 bg-[#1E1B4B] text-white text-sm font-medium rounded-xl hover:opacity-90 disabled:opacity-40 transition-all"
          data-testid="apply-coupon-btn">
          Apply
        </button>
      </div>
      {couponMessage && (<p className={`text-xs mt-1 font-medium ${couponValid ? 'text-[#25D366]' : 'text-red-500'}`}>{couponValid ? '\u2713' : '\u2717'} {couponMessage}</p>)}
    </div>
  </div>
);

export const PaymentBadges = () => <PaymentTrustBadge variant="full" className="mt-3" />;

export const LoginPromptModal = ({ show, onClose, onLogin }) => {
  if (!show) return null;
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center" onClick={e => e.stopPropagation()}>
        <div className="w-14 h-14 bg-[#E83E8C]/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <LogIn size={28} className="text-[#E83E8C]" />
        </div>
        <h3 className="text-xl font-bold text-[#1E1B4B] mb-2">Sign in to continue</h3>
        <p className="text-sm text-[#475569] mb-5">Please sign in to your DoozyGo account to complete your booking and earn loyalty rewards!</p>
        <button onClick={onLogin} className="w-full py-3 rounded-xl bg-[#E83E8C] text-white font-semibold hover:bg-[#d1356f] transition-colors mb-2" data-testid="login-to-book-btn">
          Sign In / Create Account
        </button>
        <button onClick={onClose} className="text-sm text-[#94a3b8] hover:text-[#475569]">Cancel</button>
      </div>
    </div>
  );
};

export const BookingConfirmedModal = ({ booking, onClose }) => {
  const [showCelebration, setShowCelebration] = useState(false);

  useEffect(() => {
    if (booking) {
      // Trigger balloon celebration when modal opens
      const timer = setTimeout(() => setShowCelebration(true), 300);
      return () => clearTimeout(timer);
    }
  }, [booking]);

  if (!booking) return null;
  return (
    <>
      <BalloonCelebration show={showCelebration} onComplete={() => setShowCelebration(false)} />
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="booking-confirmed-modal">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={44} className="text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-[#1E1B4B] mb-2">Booking Confirmed!</h2>
          <p className="text-[#475569] mb-6">Your party setup is booked. We'll send you confirmation via WhatsApp & Email.</p>
          <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left space-y-2">
            <div className="flex justify-between"><span className="text-sm text-[#475569]">Booking ID</span><span className="text-sm font-semibold text-[#1E1B4B]">{booking.booking_id}</span></div>
            <div className="flex justify-between"><span className="text-sm text-[#475569]">Package</span><span className="text-sm font-semibold text-[#1E1B4B]">{booking.package}</span></div>
            <div className="flex justify-between"><span className="text-sm text-[#475569]">Date</span><span className="text-sm font-semibold text-[#1E1B4B]">{new Date(booking.date).toLocaleDateString('en-IN', {day:'numeric',month:'long',year:'numeric'})}</span></div>
            <div className="flex justify-between"><span className="text-sm text-[#475569]">Setup Time</span><span className="text-sm font-semibold text-[#1E1B4B]">{booking.setup_time}</span></div>
            <div className="flex justify-between"><span className="text-sm text-[#475569]">Occasion</span><span className="text-sm font-semibold text-[#1E1B4B]">{booking.occasion}</span></div>
          </div>
          <button onClick={onClose} className="w-full py-3 rounded-xl bg-[#E83E8C] text-white font-semibold hover:bg-[#d1356f] transition-colors" data-testid="go-to-dashboard-btn">
            Go to My Dashboard
          </button>
        </div>
      </div>
    </>
  );
};
